// One agent run's detail (agents.getRun): what it was asked, how it ended, each step, its answer, and
// the model's cost for the owner only (agents.manage); a flag in words, as the approvals card says it.
// Opened from Activity today and from Recent runs, under the run's row.
// BL-189: its steps are RowList rows (the step's number before its words), a flag a red Label.

import { sentences } from '@flowology/kernel';
import { Label, RowList, type Row } from '@flowology/ui-web/fl';
import { PartSays } from './agents-parts.tsx';
import { actionWords, clock, taskText } from './model.ts';
import { costText, flagWords, runStatus, triggerWords } from './runs-model.ts';
import type { RunDetail } from './types.ts';
import { useQuery } from '../books/ui.tsx';

/** A run's step, a row: the model's turn, or the tool it called, how that went and its exact input. */
function stepRow(s: RunDetail['stepsDetail'][number]): Row {
  if (s.kind === 'model')
    return {
      id: String(s.n),
      kicker: String(s.n),
      name: s.outcome === 'answer' ? 'The model answered.' : 'The model picked its next tool.',
    };
  return {
    id: String(s.n),
    kicker: String(s.n),
    // the action in words, its dotted name on hover (F-04)
    name: (
      <>
        <span title={s.action ?? undefined}>{actionWords(s.action, s.actionTitle)}</span>{' '}
        {s.outcome.replace(/_/g, ' ')}
      </>
    ),
    ...(s.input !== null && s.input !== undefined
      ? { lines: [{ text: <code className="agents-cmd">{JSON.stringify(s.input)}</code> }] }
      : {}),
  };
}

export function RunPanel({
  runId,
  timeZone,
  showCost,
}: {
  runId: string;
  timeZone?: string | undefined;
  /** The model's cost: the owner's (agents.manage). */
  showCost: boolean;
}) {
  const run = useQuery<RunDetail>('agents.getRun', { runId });
  const r = run.data;
  return (
    <div className="agents-run">
      <PartSays
        status={r ? 'ready' : run.error ? 'failed' : 'loading'}
        what="the run"
        failedText={run.error ?? undefined}
        onRetry={() => void run.reload()}
      >
        {r && (
          <>
            <p className="agents-line">
              <b>{taskText(r.task)}</b> · {triggerWords(r.trigger)} · {runStatus(r.status)[0]}
              {r.stopReason ? ` (${r.stopReason})` : ''} · {r.steps} steps
              {showCost && r.costCents !== undefined ? ` · model cost ${costText(r.costCents)}` : ''}
            </p>
            {flagWords(r.flags).map((f) => (
              <p key={f} className="agents-line">
                <Label kind="bad">{f}</Label>
              </p>
            ))}
            {r.hidden && <p className="agents-line">{r.hidden}</p>}
            <RowList label="The run’s steps" rows={r.stepsDetail.map(stepRow)} />
            {r.answer && <p className="agents-line">Answer: {r.answer}</p>}
            <p className="agents-line" data-muted="">
              {sentences(
                `Started ${clock(r.startedAt, timeZone)}${r.endedAt ? `, ended ${clock(r.endedAt, timeZone)}` : ''}`,
              )}
            </p>
          </>
        )}
      </PartSays>
    </div>
  );
}
